import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HiOutlineMenuAlt3, HiX, HiChevronDown, HiChevronRight } from "react-icons/hi";


export const menuItems = [
    { label: 'Home', to: '/', bold: true },
    {
        label: 'About',
        children: [
            { label: 'About MIJSC', to: '/about-mijsc' },
            { label: 'About LMP', to: '/about-lmp' },
            { label: 'Academic Hub', to: '/background' },
            { label: 'Key People', to: '/key-people' },
        ]
    },
    {
        label: 'Programs',
        children: [
            { label: 'Hybrid Learning Platforms', to: '/hybrid-learning-platforms' },
            { label: 'Faculty Development Program', to: '/faculty-development-program' },
            { label: 'Student Skill Development Program', to: '/skill-development' },
            { label: 'Consultancy Program', to: '/consultancy-program' },

            { label: 'Industry Experts', to: '/industry-experts' },
            {
                label: 'Partners',
                children: [
                    { label: 'Academia', to: '/academia' },
                    { label: 'Industry', to: '/industry' },
                ]
            },

        ]
    },
    { label: 'Gallery', to: '/gallery' },
    {
        label: 'News & Events',
        children: [
            { label: 'MIJSC Programs', to: '/mijsc-programs' },
            { label: 'LMP Programs Coordinators', to: '/program-coordinators' },
            { label: 'Upcoming events', to: '/upcoming-events' },
            {
                label: 'LMP Brochures',
                to: `${process.env.PUBLIC_URL}/pdf/lmp-brochure-26.pdf`,
                external: true,
                target: '_blank',
                rel: 'noopener noreferrer'
            },
            { label: 'Participating Colleges', to: '/batches' },
        ]
    },
    { label: 'Contact Us', to: '/contact-us' },
    {
        label: 'Registration',
        to: `https://forms.gle/N2UN4Yx823XGT2he8`,
        external: true,
        target: '_blank',
        rel: 'noopener noreferrer'
    },
];


const Menu = () => {
    const [open, setOpen] = useState(false); // mobile menu
    const [openSubmenus, setOpenSubmenus] = useState({}); // track open submenus in mobile

    // Toggle mobile submenu by key
    const toggleMobileSubmenu = (key) => {
        setOpenSubmenus(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const location = useLocation();

    // Helper to check if a menu item or any of its children is active
    const isItemActive = (item) => {
        if (!item) return false;
        const normalize = (path) => path && path !== '/' ? path.replace(/\/+$/, '') : path;
        const currentPath = normalize(location.pathname);
        const itemPath = normalize(item.to);

        if (itemPath && currentPath === itemPath) return true;
        if (item.children) {
            return item.children.some(child => isItemActive(child));
        }
        return false;
    };

    // Recursive component for rendering menu items
    const MenuItem = ({ item, isMobile = false, parentKey = '' }) => {
        const hasChildren = item.children && item.children.length > 0;
        const itemKey = `${parentKey}${item.label}`;
        const isExternal = item.external;

        // Common link renderer
        const renderLink = (extraClasses = '', isActive = false, defaultColor = 'text-gray-700') => {
            const colorClass = isActive ? 'text-green-500 font-bold' : defaultColor;
            const finalClass = `${extraClasses} ${colorClass}`.trim();

            if (isExternal) {
                return (
                    <a
                        href={item.to}
                        target={item.target}
                        rel={item.rel}
                        className={finalClass}
                        onClick={() => isMobile && setOpen(false)}
                    >
                        {item.label}
                    </a>
                );
            }
            return (
                <Link
                    to={item.to || '#'}
                    className={finalClass}
                    onClick={() => isMobile && setOpen(false)}
                >
                    {item.label}
                </Link>
            );
        };

        if (isMobile) {
            const isActive = isItemActive(item);
            return (
                <li>
                    {hasChildren ? (
                        <>
                            <div
                                className={`flex justify-between cursor-pointer py-2 ${isActive ? 'text-green-600' : ''}`}
                                onClick={() => toggleMobileSubmenu(itemKey)}
                            >
                                <span className={item.bold || isActive ? 'font-bold' : ''}>{item.label}</span>
                                <HiChevronDown
                                    className={`transition-transform duration-200 text-lg ${openSubmenus[itemKey] ? 'rotate-180' : ''}`}
                                />
                            </div>
                            {openSubmenus[itemKey] && (
                                <ul className="ml-6 mt-1 flex flex-col gap-3 border-l-2 border-green-200 pl-4">
                                    {item.children.map((child, idx) => (
                                        <MenuItem key={idx} item={child} isMobile={true} parentKey={`${itemKey}-`} />
                                    ))}
                                </ul>
                            )}
                        </>
                    ) : (
                        renderLink('', isActive, 'text-gray-700')
                    )}
                </li>
            );
        }

        // Desktop version
        const isActive = isItemActive(item);
        return (
            <li className="relative group">
                {hasChildren ? (
                    <>
                        <span className={`font-bold text-sm flex items-center gap-1 cursor-pointer transition-colors ${isActive ? 'text-green-500' : 'text-white'}`}>
                            {item.label} <HiChevronDown className={`text-sm ${isActive ? 'text-green-500' : 'text-white'}`} />
                        </span>
                        <div className="absolute left-0 top-full pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 min-w-[180px] z-50">
                        <ul className="bg-white text-gray-800 rounded-md shadow-lg min-w-[180px]">
                            {item.children.map((child, idx) => {
                                const childHasChildren = child.children && child.children.length > 0;
                                const childIsExternal = child.external;
                                const childActive = isItemActive(child);

                                return (
                                    <li key={idx} className="text-xs relative group/sub">
                                        {childHasChildren ? (
                                            <>
                                                <span className={`block px-4 py-2 hover:bg-green-100 flex items-center justify-between ${childActive ? 'text-green-600 bg-green-50' : ''}`}>
                                                    {child.label}
                                                    <HiChevronRight className="text-sm" />
                                                </span>
                                                <ul className="absolute left-full top-0 mt-[-2px] bg-white text-gray-800 rounded-md shadow-lg opacity-0 group-hover/sub:opacity-100 transition-opacity duration-200 min-w-[180px]">
                                                    {child.children.map((grandchild, gidx) => {
                                                        const grandchildActive = isItemActive(grandchild);
                                                        return (
                                                            <li key={gidx}>
                                                                {grandchild.external ? (
                                                                    <a
                                                                        href={grandchild.to}
                                                                        target={grandchild.target}
                                                                        rel={grandchild.rel}
                                                                        className={`block px-4 py-2 hover:bg-green-100 text-left ${grandchildActive ? 'text-green-600 font-bold' : ''}`}
                                                                    >
                                                                        {grandchild.label}
                                                                    </a>
                                                                ) : (
                                                                    <Link
                                                                        to={grandchild.to || '#'}
                                                                        className={`block px-4 py-2 hover:bg-green-100 text-left ${grandchildActive ? 'text-green-600 font-bold' : ''}`}
                                                                    >
                                                                        {grandchild.label}
                                                                    </Link>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </>
                                        ) : (
                                            childIsExternal ? (
                                                <a
                                                    href={child.to}
                                                    target={child.target}
                                                    rel={child.rel}
                                                    className={`block px-4 py-2 hover:bg-green-100 text-left ${childActive ? 'text-green-600 font-bold bg-green-50' : ''}`}
                                                >
                                                    {child.label}
                                                </a>
                                            ) : (
                                                <Link
                                                    to={child.to || '#'}
                                                    className={`block px-4 py-2 hover:bg-green-100 text-left ${childActive ? 'text-green-600 font-bold bg-green-50' : ''}`}
                                                >
                                                    {child.label}
                                                </Link>
                                            )
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        </div>
                    </>
                ) : (
                    renderLink('font-bold text-sm text-sm', isActive, 'text-white')
                )}
            </li>
        );
    };

    return (
        <div className="relative mx-3">

            {/* Desktop Menu */}
            <div className="menu hidden md:block">
                <nav>
                    <ul className="text-white flex gap-12 items-center">
                        {menuItems.map((item, idx) => (
                            <MenuItem key={idx} item={item} />
                        ))}
                    </ul>
                </nav>
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden">
                <HiOutlineMenuAlt3
                    className="text-white text-3xl cursor-pointer"
                    onClick={() => setOpen(true)}
                />
            </div>

            {/* Backdrop */}
            {open && (
                <div
                    className="fixed inset-0 bg-black/60 z-40"
                    onClick={() => {
                        setOpen(false);
                        setOpenSubmenus({});
                    }}
                />
            )}

            {/* Mobile Menu Sidebar */}
            <div
                className={`fixed top-0 right-0 h-full w-72 bg-white z-50 transform transition-transform duration-300 overflow-y-auto
                    ${open ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <span className="font-bold text-lg">Menu</span>
                    <HiX className="text-2xl cursor-pointer" onClick={() => setOpen(false)} />
                </div>

                <ul className="flex flex-col gap-5 p-5 font-semibold text-gray-800 text-left">
                    {menuItems.map((item, idx) => (
                        <MenuItem key={idx} item={item} isMobile={true} parentKey="mobile-" />
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default Menu;